import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Calculator, Plus, Pencil, Trash2, RotateCcw, X, Truck, Download, Copy, Package, TrendingUp, Ruler, Skull, AlertTriangle, Lock, Info, Mail, Calendar, Send } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import TemplateManager from "@/components/TemplateManager";
import VestaBadge from "@/components/VestaBadge";
import FissaggiTettoDialog from "@/components/FissaggiTettoDialog";
import MagazzinoDetailDialog from "@/components/MagazzinoDetailDialog";
import ErrorBoundary from "@/components/ErrorBoundary";
import { EmailQuoteDialog } from "@/components/EmailQuoteDialog";
import PianoConsegne from "@/components/PianoConsegne";
import { openPdfBlob } from "@/lib/pdf";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { format } from "date-fns";

const eurN = (v) => (v == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(+v || 0));
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const STATI = ["Bozza", "Da Approvare", "Aperto", "Chiuso", "Rifiutato"];
const STATO_COLOR = { Bozza: "outline", "Da Approvare": "secondary", Aperto: "default", Chiuso: "default", Rifiutato: "destructive" };

// Input data nativo: apre il selettore nativo del sistema (affidabile su mobile iOS/Android e desktop).
const DateField = ({ value, onChange, invalid, testid, min }) => {
  const ref = useRef(null);
  const val = value ? String(value).slice(0, 10) : "";
  const openPicker = () => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.showPicker === "function") { try { el.showPicker(); return; } catch (e) { /* fallback below */ } }
    el.focus();
    el.click();
  };
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testid}
      onClick={openPicker}
      className={`relative flex h-10 w-full items-center rounded-md border bg-background px-3 text-sm font-normal cursor-pointer ${invalid ? "border-red-400" : "border-input"} ${!val ? "text-muted-foreground" : ""}`}
    >
      <Calendar className="mr-2 h-4 w-4 shrink-0 opacity-70" />
      <span className="flex-1">{val ? val.split("-").reverse().join("/") : "Seleziona data"}</span>
      <input
        ref={ref}
        type="date"
        value={val}
        min={min}
        onChange={(e) => onChange(e.target.value || "")}
        className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
      />
    </div>
  );
};
const NEW = () => ({ cliente: "", cliente_type: null, cliente_is_partner: false, partner_id: null, prospect_id: null, date: today(), data_richiesta: "", stato: "Bozza", iva_pct: 10, sconto_globale_pct: 0, note: "", righe: [], tetto_config: null, tipo_ordine: "spot", modalita_pagamento: "", modalita_default_cliente: "", trasporto_zone: "", trasporto_fasci: false, trasporto_non_sovrapponibile: false, trasporto_non_sovr_manuale: false, trasporto_adr: false, trasporto_eur: 0, trasporto_dettaglio: null, consegna_tipo: "vettore", consegna_modo: "unica", consegna_piano: [], consegna_sede_cliente: false, consegna_indirizzo: "", consegna_cap: "", consegna_citta: "", consegna_provincia: "" });

const fmtPct = (v) => (v == null ? "—" : v.toLocaleString("it-IT", { maximumFractionDigits: 1 }) + "%");
const icColorClass = (ic, cap = 10) => {
  if (ic == null) return "";
  const hi = cap * 0.5, mid = cap * 0.2;
  if (ic >= hi) return "border-emerald-300 bg-emerald-100 text-emerald-800";
  if (ic >= mid) return "border-amber-300 bg-amber-100 text-amber-800";
  return "border-red-300 bg-red-100 text-red-700";
};

// Accento colore per categoria (barra laterale, testo, chip totale) — leggibile su tema chiaro e scuro.
const fmtDM = (iso) => { if (!iso) return ""; const p = String(iso).slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}/${p[1]}` : iso; };
const availMeta = (av) => {
  if (!av || av.stato === "sconosciuto") return { color: "text-muted-foreground bg-muted", label: "n/d magazzino" };
  if (av.stato === "disponibile") return { color: "text-emerald-700 bg-emerald-400/15 dark:text-emerald-300", label: "Disponibile" };
  if (av.stato === "da_ordinare") return { color: "text-red-700 bg-red-400/15 dark:text-red-300", label: "Contattare Back Office" };
  return { color: "text-yellow-700 bg-yellow-400/20 dark:text-yellow-300", label: `In approntamento entro ${fmtDM(av.data_piena)}` };
};
const availDetail = (av) => {
  if (!av || !av.breakdown) return "";
  const parts = (av.breakdown || []).map((b) => b.data ? `${b.qta} in approntamento entro ${fmtDM(b.data)}${b.in_ritardo ? " (in ritardo)" : ""}` : `${b.qta} disponibili subito`);
  if (av.scoperto > 0) parts.push(`${av.scoperto} pz — contattare Back Office`);
  return parts.join(" · ");
};

const splitLots = (righe, avail) => {
  const byCode = {};
  (avail?.righe || []).forEach((x) => { byCode[(x.codice || "").toLowerCase()] = x; });
  const subito = [], saldo = [];
  (righe || []).forEach((r) => {
    const qn = +r.quantita || 0;
    if (!r.codice || qn <= 0) { if (qn > 0) saldo.push({ ...r }); return; }
    const av = byCode[(r.codice || "").toLowerCase()];
    if (!av || av.stato === "sconosciuto") { saldo.push({ ...r }); return; }
    const subQ = (av.breakdown || []).filter((b) => !b.data).reduce((a, b) => a + (+b.qta || 0), 0);
    const sQ = Math.min(subQ, qn), rem = qn - sQ;
    if (sQ > 0) subito.push({ ...r, quantita: sQ });
    if (rem > 0) saldo.push({ ...r, quantita: rem });
  });
  return { subito, saldo };
};

// Firma della configurazione fissaggi: solo righe bloccate (modulo + fissaggi), codice=q.tà ordinati.
const lockSig = (rows) => (rows || []).filter((r) => r.locked).map((r) => `${(r.codice || "").trim().toLowerCase()}=${+r.quantita || 0}`).sort().join("|");

const catAccent = (cat) => {
  return { head: "bg-yellow-400/10", rail: "border-yellow-400", railSoft: "border-yellow-400/30", text: "text-yellow-600 dark:text-yellow-300", chip: "border-yellow-400/40 bg-yellow-400/15 text-yellow-700 dark:text-yellow-300" };
};

const computeTotals = (righe, ivaPct, scontoGlob, trasporto = 0, raeeFee = 0, marginCalc = null) => {
  let imponibile = 0, kwp = 0, costo = 0, nMod = 0;
  righe.forEach((r) => {
    const q = +r.quantita || 0, pu = +r.prezzo_unitario || 0, sc = +r.sconto_pct || 0, cu = +r.costo_unitario || 0, kw = +r.kwp || 0;
    imponibile += q * pu * (1 - sc / 100); kwp += q * kw; costo += q * cu;
    const tp = (r.tipologia || "").toLowerCase(), ds = (r.descrizione || "").toLowerCase();
    const acc = tp.includes("accumul") || ds.includes("accumul");
    if (!acc && (tp.includes("modul") || ds.includes("modulo fotovolt") || kw > 0)) nMod += q;
  });
  const sg = +scontoGlob || 0, impS = imponibile * (1 - sg / 100);
  const trasp = +trasporto || 0;
  const raee = (+raeeFee || 0) * nMod;
  const iva = +ivaPct || 0, baseIva = impS + trasp + raee, ivaE = baseIva * iva / 100, tot = baseIva + ivaE, margine = impS - costo;
  let contrib = {};
  if (marginCalc && marginCalc.sconto_applicabile_eur != null) {
    // Valori autorevoli dal server: target% e sconto applicabile (indipendenti dallo sconto globale).
    // Il costo è derivato da questi valori senza mai esporre i costi grezzi al client.
    const t = +marginCalc.margine_target_pct || 0;
    const S = +marginCalc.sconto_applicabile_eur || 0;
    const cap = marginCalc.cap != null ? +marginCalc.cap : 10;
    const C = Math.max(0, (imponibile - S) * (1 - t / 100));
    const marginePct = impS ? (impS - C) / impS * 100 : 0;
    const ic = Math.max(0, Math.min(cap, marginePct - t));
    contrib = { margine_target_pct: t, indice_contribuzione: ic, sconto_applicabile_eur: S };
  }
  if (marginCalc && marginCalc.provvigione_pct != null) {
    const pp = +marginCalc.provvigione_pct || 0;
    contrib.provvigione_pct = pp;
    contrib.provvigione_eur = impS * pp / 100;
  }
  return { imponibile, imponibile_scontato: impS, trasporto_eur: trasp, raee_eur: raee, raee_moduli: nMod, iva_eur: ivaE, totale: tot, kwp_totale: kwp, costo_totale: costo, margine_eur: margine, margine_pct: impS ? margine / impS * 100 : 0, ...contrib };
};
const lineTotal = (r) => (+r.quantita || 0) * (+r.prezzo_unitario || 0) * (1 - (+r.sconto_pct || 0) / 100);

export default function Preventivatore() {
  return (
    <ErrorBoundary
      title="Impossibile aprire il preventivo"
      hint={(
        <div className="space-y-2">
          <p>Questo può accadere quando il <b>layout della consulenza non è stato confermato</b> nel configuratore.</p>
          <p className="font-semibold text-foreground">Cosa fare:</p>
          <ol className="list-decimal space-y-0.5 pl-5">
            <li>Torna alla consulenza nel configuratore.</li>
            <li>Premi <b>«Usa layout»</b> per confermare il layout del tetto.</li>
            <li>Riapri il preventivo.</li>
          </ol>
          <p>Se il problema persiste, contatta il Back Office.</p>
        </div>
      )}
    >
      <PreventivatoreInner />
    </ErrorBoundary>
  );
}

function PreventivatoreInner() {
  const { user } = useAuth();
  const [data, setData] = useState({ quotes: [], can_manage: false });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search: "", stato: "all" });
  const [q, setQ] = useState(null); // composer state (null = closed)
  const [saving, setSaving] = useState(false);
  // cliente autocomplete
  const [cliQ, setCliQ] = useState("");
  const [cliRes, setCliRes] = useState([]);
  const cliTimer = useRef(null);
  // listino picker
  const [lp, setLp] = useState("");
  const [lpRes, setLpRes] = useState([]);
  const lpTimer = useRef(null);
  const [servizi, setServizi] = useState([]);
  useEffect(() => { api.get("/quote-services").then((r) => setServizi(r.data.servizi || [])).catch(() => {}); }, []);
  // trasporto
  const [provinces, setProvinces] = useState([]);
  const [calcTrasp, setCalcTrasp] = useState(false);
  const [avail, setAvail] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [magDetail, setMagDetail] = useState(null);
  const [gate, setGate] = useState(null);
  const [payMethods, setPayMethods] = useState([]);
  useEffect(() => { api.get("/payment-methods").then((r) => setPayMethods(r.data.methods || [])).catch(() => {}); }, []);
  useEffect(() => {
    const url = q?.partner_id ? `/partners/${q.partner_id}/pagamento-default` : q?.prospect_id ? `/prospects/${q.prospect_id}/pagamento-default` : null;
    if (url) api.get(url).then((r) => setQ((s) => (s ? { ...s, modalita_default_cliente: r.data.default || "" } : s))).catch(() => {});
  }, [q?.partner_id, q?.prospect_id]);
  const [gating, setGating] = useState(false);
  useEffect(() => { api.get("/transport/provinces").then((r) => setProvinces(r.data)).catch(() => {}); }, []);
  // template
  const [templates, setTemplates] = useState([]);
  const [tmplMgr, setTmplMgr] = useState(false);
  const [emailQuote, setEmailQuote] = useState(null);
  const [cloneRow, setCloneRow] = useState(null);
  const loadTemplates = useCallback(() => api.get("/quote-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {}), []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (f.search) p.set("search", f.search);
    if (f.stato !== "all") p.set("stato", f.stato);
    api.get(`/crm-quotes?${p.toString()}`).then((r) => setData(r.data)).catch(() => toast.error("Errore caricamento")).finally(() => setLoading(false));
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const canMgmt = data.can_manage;
  const canFin = data.can_view_financials || data.can_manage;
  const attivi = (data.quotes || []).filter((x) => x.stato !== "Rifiutato");
  const cimitero = (data.quotes || []).filter((x) => x.stato === "Rifiutato");
  const isBO = data.can_use_dedicato;
  const canMagDetail = ["admin", "back_office", "head_of_partner"].includes(user?.role);
  const fissLock = q?.tetto_config?.lock || null;
  const fissLockValido = !!(fissLock && fissLock.signature && fissLock.locked && lockSig(q?.righe) === fissLock.signature);
  const [marginCalc, setMarginCalc] = useState(null);
  const [scontoMode, setScontoMode] = useState("pct");
  const totals = q ? computeTotals(q.righe, q.iva_pct, q.sconto_globale_pct, q.trasporto_eur, data.raee_eur_modulo, marginCalc ? { ...marginCalc, cap: data.margin_config?.cap } : null) : null;
  const scontoApplEur = totals?.sconto_applicabile_eur;
  const scontoMaxEur = scontoApplEur != null ? scontoApplEur * 1.1 : null;               // limite esteso: +10% oltre lo sconto applicabile
  const scontoMaxPct = (scontoMaxEur != null && totals?.imponibile > 0) ? scontoMaxEur / totals.imponibile * 100 : null;
  const appliedScontoEur = (q && totals) ? (totals.imponibile || 0) * (+q.sconto_globale_pct || 0) / 100 : 0;
  const sottoMargine = scontoApplEur != null && appliedScontoEur > scontoApplEur + 0.01;   // oltre il margine minimo → Back Office
  const availByCode = {};
  (avail?.righe || []).forEach((x) => { availByCode[(x.codice || "").toLowerCase()] = x; });
  const lineGroups = q ? (() => {
    const m = new Map();
    (q.righe || []).forEach((r, i) => {
      const key = ((r.tipologia || "").trim()) || "Senza categoria";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push({ r, i });
    });
    const rank = (c) => {
      const s = (c || "").toLowerCase();
      if (s.includes("modul")) return 0;
      if (s.includes("inverter")) return 1;
      if (s.includes("accumul") || s.includes("batter")) return 2;
      if (s.includes("fissagg")) return 3;
      return 4;
    };
    return Array.from(m.entries()).sort((a, b) => (rank(a[0]) - rank(b[0])) || a[0].localeCompare(b[0]));
  })() : [];

  // Calcolo autorevole (lato server) di target margine e sconto applicabile: indipendenti dallo sconto globale.
  useEffect(() => {
    if (!q || !(q.righe || []).length) { setMarginCalc(null); return; }
    const body = {
      righe: q.righe.map((r) => ({ listino_id: r.listino_id, tipologia: r.tipologia, quantita: r.quantita, prezzo_unitario: r.prezzo_unitario, sconto_pct: r.sconto_pct })),
      iva_pct: q.iva_pct, sconto_globale_pct: 0, trasporto_eur: 0, agency_id: q.agency_id, tipo_ordine: q.tipo_ordine || "spot",
    };
    const tmr = setTimeout(() => { api.post("/crm-quotes/compute", body).then((r) => setMarginCalc(r.data)).catch(() => {}); }, 350);
    return () => clearTimeout(tmr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q?.righe, q?.tipo_ordine]);

  const applySedeCliente = async (checked) => {
    if (!checked) { setQ((s) => ({ ...s, consegna_sede_cliente: false })); return; }
    const type = q.partner_id ? "partner" : (q.prospect_id ? "prospect" : null);
    const id = q.partner_id || q.prospect_id;
    if (!id) { toast.error("Collega prima un cliente Partner/Prospect"); return; }
    try {
      const r = await api.get(`/crm-quotes/client-address?type=${type}&id=${id}`);
      const a = r.data || {};
      setQ((s) => ({ ...s, consegna_sede_cliente: true, consegna_indirizzo: a.indirizzo || "", consegna_cap: a.cap || "", consegna_citta: a.citta || "", consegna_provincia: a.provincia || "", trasporto_zone: a.provincia || "" }));
      if (!a.provincia) toast.warning("La scheda cliente non ha una provincia: selezionala manualmente");
    } catch { toast.error("Errore recupero indirizzo cliente"); }
  };

  // calcolo trasporto automatico (debounce) in base a provincia/righe/supplementi
  useEffect(() => {
    if (!q) return;
    if (q.tipo_ordine === "programmato") { setCalcTrasp(false); return; }
    if (q.consegna_tipo === "dedicato") { setCalcTrasp(false); return; }
    if (q.consegna_tipo === "ritiro") {
      if ((+q.trasporto_eur || 0) !== 0 || q.trasporto_dettaglio) setQ((s) => ({ ...s, trasporto_eur: 0, trasporto_dettaglio: null }));
      return;
    }
    if (!q.trasporto_zone || !(q.righe || []).length) return;
    setCalcTrasp(true);
    const tmr = setTimeout(async () => {
      try {
        const t = computeTotals(q.righe, q.iva_pct, q.sconto_globale_pct, 0);
        const mkPayload = (righe, valore) => ({
          righe: righe.map((x) => ({ listino_id: x.listino_id, codice: x.codice, tipologia: x.tipologia, quantita: x.quantita })),
          zone: q.trasporto_zone, valore_merce: valore,
          non_sovrapponibile_manuale: !!q.trasporto_non_sovr_manuale,
        });
        if (q.consegna_modo === "parziale" && avail?.righe?.length) {
          const { subito, saldo } = splitLots(q.righe, avail);
          if (subito.length && saldo.length) {
            const vTot = t.imponibile_scontato || 0;
            const [r1, r2] = await Promise.all([
              api.post("/transport/calculate", mkPayload(subito, vTot)),
              api.post("/transport/calculate", mkPayload(saldo, vTot)),
            ]);
            const d1 = r1.data, d2 = r2.data;
            const tot = (d1.calcolabile ? d1.totale : 0) + (d2.calcolabile ? d2.totale : 0);
            const f1 = d1.flags || {}, f2 = d2.flags || {};
            setQ((s) => ({ ...s, trasporto_eur: tot, trasporto_dettaglio: { calcolabile: (d1.calcolabile || d2.calcolabile), totale: tot, parziale: true, lotti: [{ label: "Merce pronta subito", det: d1, items: subito, data: null }, { label: "Saldo", det: d2, items: saldo, data: avail?.data_piena || avail?.data_completa || null }], prodotti_senza_dati: [...(d1.prodotti_senza_dati || []), ...(d2.prodotti_senza_dati || [])] }, trasporto_fasci: !!(f1.fasci || f2.fasci), trasporto_non_sovrapponibile: !!(f1.non_sovrapponibile || f2.non_sovrapponibile), trasporto_adr: !!(f1.adr || f2.adr) }));
            return;
          }
        }
        const r = await api.post("/transport/calculate", mkPayload(q.righe, t.imponibile_scontato));
        const res = r.data;
        const fl = res.flags || {};
        setQ((s) => ({ ...s, trasporto_eur: res.calcolabile ? res.totale : 0, trasporto_dettaglio: res, trasporto_fasci: !!fl.fasci, trasporto_non_sovrapponibile: !!fl.non_sovrapponibile, trasporto_adr: !!fl.adr }));
      } catch { /* silenzioso */ }
      finally { setCalcTrasp(false); }
    }, 500);
    return () => clearTimeout(tmr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q?.trasporto_zone, q?.righe, q?.consegna_tipo, q?.consegna_modo, q?.trasporto_non_sovr_manuale, avail]);

  // disponibilità magazzino (ATP) automatica in base a righe + data richiesta
  useEffect(() => {
    if (!q) { setAvail(null); return; }
    const codes = (q.righe || []).filter((r) => r.codice && (+r.quantita || 0) > 0);
    if (!codes.length) { setAvail(null); return; }
    setAvailLoading(true);
    const tmr = setTimeout(async () => {
      try {
        const r = await api.post("/magazzino/availability", { data_richiesta: q.data_richiesta || null, righe: codes.map((x) => ({ codice: x.codice, quantita: x.quantita })) });
        setAvail(r.data);
      } catch { setAvail(null); }
      finally { setAvailLoading(false); }
    }, 500);
    return () => clearTimeout(tmr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q?.righe, q?.data_richiesta]);

  const searchCliente = (v) => {
    setCliQ(v); setQ((s) => ({ ...s, cliente: v, cliente_type: null, cliente_is_partner: false, partner_id: null, prospect_id: null }));
    clearTimeout(cliTimer.current);
    if (v.trim().length < 2) { setCliRes([]); return; }
    cliTimer.current = setTimeout(() => {
      api.get(`/crm-quotes/clienti?q=${encodeURIComponent(v)}`).then((r) => setCliRes(r.data)).catch(() => setCliRes([]));
    }, 300);
  };
  const pickCliente = (c) => {
    setQ((s) => ({ ...s, cliente: c.name, cliente_type: c.type, cliente_is_partner: !!c.is_partner, partner_id: c.type === "partner" ? c.id : null, prospect_id: c.type === "prospect" ? c.id : null }));
    setCliRes([]); setCliQ(c.name);
  };

  const searchListino = (v) => {
    setLp(v); clearTimeout(lpTimer.current);
    if (v.trim().length < 2) { setLpRes([]); return; }
    lpTimer.current = setTimeout(() => {
      api.get(`/listino?abilitato=1&search=${encodeURIComponent(v)}`).then((r) => setLpRes(r.data.products || [])).catch(() => setLpRes([]));
    }, 300);
  };
  const addFromListino = (p) => {
    setQ((s) => ({ ...s, righe: [...s.righe, { listino_id: p.id, codice: p.codice, descrizione: p.descrizione, tipologia: p.tipologia, um: p.um || "Pz", quantita: 1, prezzo_unitario: p.prezzo || 0, sconto_pct: 0, kwp: p.kwp || 0, costo_unitario: p.costo || 0 }] }));
    setLp(""); setLpRes([]);
  };
  const servizioPrezzo = (s, kwpTot) => {
    const tipo = s.tipo || "fisso";
    if (tipo === "manuale") return 0;
    if (tipo !== "pratica") return +s.prezzo || 0;
    const sc = [...(s.scaglioni || [])].filter((x) => x && x.prezzo != null && x.fino_a_kwp != null).sort((a, b) => (+a.fino_a_kwp || 0) - (+b.fino_a_kwp || 0));
    if (!sc.length) return null;
    const hit = sc.find((x) => kwpTot <= (+x.fino_a_kwp || 0));
    return hit ? (+hit.prezzo || 0) : null;
  };
  const addServizio = (codice) => {
    const s = servizi.find((x) => x.codice === codice);
    if (!s) return;
    const tipo = s.tipo || "fisso";
    const kwpTot = (q.righe || []).reduce((a, r) => a + (+r.kwp || 0) * (+r.quantita || 0), 0);
    const prezzo = servizioPrezzo(s, kwpTot);
    if (tipo === "pratica" && prezzo == null) {
      toast.error(`Impianto da ${kwpTot.toFixed(2)} kWp fuori dalle fasce previste per "${s.codice}": servizio non gestibile in autonomia, contatta il Back Office.`);
      return;
    }
    setQ((st) => ({ ...st, righe: [...st.righe, { listino_id: null, codice: s.codice, descrizione: s.nome, tipologia: "Servizi", um: s.um || "Nr", quantita: 1, prezzo_unitario: prezzo || 0, sconto_pct: 0, kwp: 0, costo_unitario: 0, servizio: true, servizio_manuale: tipo === "manuale" }] }));
    if (tipo === "pratica") toast.success(`"${s.codice}" aggiunto: ${eurN(prezzo || 0)} per impianto da ${kwpTot.toFixed(2)} kWp`);
    else if (tipo === "manuale") toast.message(`"${s.codice}" aggiunto — prezzo da definire dal Back Office`);
  };
  const serviziObbMancanti = (righe) => servizi.filter((s) => {
    const mob = (s.moduli_obbligatori || []).map((x) => String(x).toLowerCase());
    if (!mob.length) return false;
    const codes = (righe || []).map((r) => (r.codice || "").toLowerCase());
    return mob.some((m) => codes.includes(m)) && !codes.includes((s.codice || "").toLowerCase());
  });
  // Ricalcolo automatico prezzo pratiche (es. Schema Unifilare) al variare dei kWp impianto
  const prevSign = servizi.length ? JSON.stringify((q?.righe || []).map((r) => [r.codice, r.quantita, r.kwp])) : "";
  useEffect(() => {
    if (!q || !servizi.length) return;
    const kwpTot = (q.righe || []).reduce((a, r) => a + (+r.kwp || 0) * (+r.quantita || 0), 0);
    let changed = false; const notes = [];
    const righe = (q.righe || []).map((r) => {
      const s = servizi.find((x) => x.codice === r.codice && (x.tipo === "pratica"));
      if (!s) return r;
      const p = servizioPrezzo(s, kwpTot);
      if (p == null) {
        if (+r.prezzo_unitario !== 0 || !r._fuoriFascia) { changed = true; notes.push(`${s.codice}: ${kwpTot.toFixed(2)} kWp fuori fascia (contatta il BO)`); return { ...r, prezzo_unitario: 0, _fuoriFascia: true }; }
        return r;
      }
      if (+r.prezzo_unitario !== p || r._fuoriFascia) { changed = true; notes.push(`${s.codice} → ${eurN(p)} (${kwpTot.toFixed(2)} kWp)`); return { ...r, prezzo_unitario: p, _fuoriFascia: false }; }
      return r;
    });
    if (changed) { setQ((st) => ({ ...st, righe })); toast.message(`Servizio aggiornato: ${notes.join(" · ")}`); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevSign, servizi]);
  const setLine = (i, patch) => setQ((s) => ({ ...s, righe: s.righe.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const removeLine = (i) => setQ((s) => ({ ...s, righe: s.righe.filter((_, j) => j !== i) }));

  const openNew = () => { setQ(NEW()); setCliQ(""); setCliRes([]); setLp(""); setLpRes([]); setGate(null); setAvail(null); };
  const openEdit = async (row) => {
    setGate(null); setAvail(null);
    try {
      const r = await api.get(`/crm-quotes/${row.id}`);
      const t = r.data.trasporto || {};
      const cons = r.data.consegna || {};
      setQ({ ...NEW(), ...r.data, modalita_pagamento: r.data.modalita_pagamento || "", trasporto_zone: t.zone || cons.provincia || "", trasporto_fasci: !!t.fasci, trasporto_non_sovrapponibile: !!t.non_sovrapponibile, trasporto_non_sovr_manuale: !!t.non_sovr_manuale, trasporto_adr: !!t.adr, trasporto_eur: r.data.trasporto_eur || 0, trasporto_dettaglio: t.dettaglio || null, consegna_tipo: cons.tipo || "vettore", consegna_modo: cons.modo || "unica", consegna_piano: cons.piano || [], consegna_sede_cliente: !!cons.sede_cliente, consegna_indirizzo: cons.indirizzo || "", consegna_cap: cons.cap || "", consegna_citta: cons.citta || "", consegna_provincia: cons.provincia || "", righe: (r.data.righe || []).map((x) => ({ ...x })) });
      setCliQ(r.data.cliente || ""); setCliRes([]); setLp(""); setLpRes([]);
    } catch { toast.error("Errore apertura preventivo"); }
  };

  const buildBody = () => {
    const isRitiro = q.consegna_tipo === "ritiro";
    return { cliente: q.cliente, cliente_type: q.cliente_type, partner_id: q.partner_id, prospect_id: q.prospect_id, tetto_config: q.tetto_config || null, tipo_ordine: q.tipo_ordine || "spot", modalita_pagamento: q.modalita_pagamento || "", date: q.date, data_richiesta: q.data_richiesta || null, stato: q.stato, iva_pct: +q.iva_pct || 0, sconto_globale_pct: +q.sconto_globale_pct || 0, note: q.note, righe: q.righe, trasporto_eur: isRitiro ? 0 : (+q.trasporto_eur || 0), trasporto: (!isRitiro && (q.trasporto_zone || q.consegna_tipo === "dedicato")) ? { zone: q.trasporto_zone, fasci: q.trasporto_fasci, non_sovrapponibile: q.trasporto_non_sovrapponibile, non_sovr_manuale: q.trasporto_non_sovr_manuale, adr: q.trasporto_adr, dettaglio: q.trasporto_dettaglio } : null, consegna: { tipo: q.consegna_tipo || "vettore", modo: q.consegna_modo || "unica", piano: q.consegna_piano || [], sede_cliente: !!q.consegna_sede_cliente, indirizzo: q.consegna_indirizzo || "", cap: q.consegna_cap || "", citta: q.consegna_citta || "", provincia: q.consegna_provincia || q.trasporto_zone || "" } };
  };
  const validate = () => {
    if (!q.cliente.trim()) { toast.error("Indica il cliente"); return false; }
    if (!q.data_richiesta) { toast.error("Indica la data richiesta di consegna"); return false; }
    if (!q.righe.length) { toast.error("Aggiungi almeno una riga"); return false; }
    const _obbMiss = serviziObbMancanti(q.righe);
    if (_obbMiss.length) { toast.error(`Servizio obbligatorio mancante: ${_obbMiss.map((s) => s.codice).join(", ")}. I moduli selezionati lo richiedono: aggiungilo.`); return false; }
    if (q.tipo_ordine === "programmato" && q.tetto_config) { toast.error("L'ordine programmato non è disponibile per i preventivi da Configuratore"); return false; }
    if (q.tipo_ordine === "programmato") {
      const impListino = (q.righe || []).reduce((a, r) => a + (+r.prezzo_unitario || 0) * (+r.quantita || 0), 0);
      if (impListino < 30000) { toast.error(`Ordine programmato non disponibile: importo di listino ${eurN(impListino)} inferiore a 30.000 €`); return false; }
      const p = q.consegna_piano || [];
      if (p.length < 2) { toast.error("Ordine programmato: inserisci almeno 2 consegne nel piano di approvvigionamento"); return false; }
      if (p.some((c) => !c.data)) { toast.error("Ogni consegna del piano deve avere una data"); return false; }
      if (p[0]?.data && p[1]?.data) {
        const diff = Math.abs(new Date(p[1].data) - new Date(p[0].data)) / 86400000;
        if (diff < 21) { toast.error("Tra la 1ª e la 2ª consegna devono passare almeno 3 settimane (21 giorni)"); return false; }
      }
      if (q.consegna_tipo === "dedicato" && p.some((c) => !(+c.prezzo_manuale > 0))) { toast.error("Trasporti dedicati: inserisci il prezzo di ogni consegna"); return false; }
      const want = {}; (q.righe || []).forEach((r) => { if (r.codice) { const k = r.codice.toLowerCase(); want[k] = (want[k] || 0) + (+r.quantita || 0); } });
      const got = {}; p.forEach((c) => (c.righe || []).forEach((rr) => { const k = (rr.codice || "").toLowerCase(); got[k] = (got[k] || 0) + (+rr.qta || 0); }));
      const keys = new Set([...Object.keys(want), ...Object.keys(got)]);
      if ([...keys].some((k) => (got[k] || 0) !== (want[k] || 0))) { toast.error("Allocazione articoli incompleta: assegna tutte le quantità del preventivo alle consegne"); return false; }
    }
    return true;
  };
  // Salva lo stato corrente senza chiudere il dialog; ritorna l'id del preventivo.
  const persist = async () => {
    const body = buildBody();
    if (q.id) { await api.put(`/crm-quotes/${q.id}`, body); return q.id; }
    const r = await api.post("/crm-quotes", body);
    const newId = r.data?.id;
    setQ((s) => ({ ...s, id: newId }));
    return newId;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await persist();
      toast.success("Preventivo salvato"); setQ(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const valutaPreventivo = async () => {
    if (!validate()) return;
    setGating(true);
    let id;
    try {
      id = await persist();                 // salva le modifiche correnti PRIMA di valutare
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Errore salvataggio preventivo prima della valutazione");
      setGating(false);
      return;
    }
    try {
      const r = await api.post(`/crm-quotes/${id}/valuta`);
      setGate(r.data);
    } catch (err) {
      const status = err.response?.status;
      const d = err.response?.data?.detail;
      let msg;
      if (err.code === "ECONNABORTED" || status === 504 || status === 520) {
        msg = "Valutazione troppo lenta (timeout server): riprova tra poco.";
      } else if (typeof d === "string") {
        msg = d;
      } else if (d?.message) {
        msg = d.message;
      } else if (status >= 500) {
        msg = "Errore interno durante la valutazione del preventivo. Riprova o contatta il Back Office.";
      } else {
        msg = "Errore valutazione";
      }
      toast.error(msg);
    } finally { setGating(false); }
  };
  const apriPreventivo = async (force) => {
    let motivo = ""; let rendi_default = false;
    if (force) { motivo = window.prompt("Motivo apertura forzata (obbligatorio):") || ""; if (!motivo.trim()) return; }
    if (force && gate?.modalita_info?.diversa) {
      rendi_default = window.confirm(`Rendere «${gate.modalita_info.scelta}» la modalità di pagamento di DEFAULT per questo cliente?\n\nOK = imposta come nuovo default · Annulla = approva solo per questo preventivo`);
    }
    try {
      const id = await persist();          // allinea lo stato salvato alle modifiche correnti
      const r = await api.post(`/crm-quotes/${id}/apri`, { force, motivo, rendi_default });
      setQ((s) => ({ ...s, stato: r.data.stato, numero: r.data.numero }));
      setGate(null);
      toast.success(`Preventivo aperto${r.data.numero ? ` · ${r.data.numero}` : ""}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Apertura non consentita"));
    }
  };
  const inviaBackoffice = async () => {
    if (!validate()) return;
    try {
      const id = await persist();
      const r = await api.post(`/crm-quotes/${id}/invia-backoffice`);
      setQ((s) => ({ ...s, stato: r.data.stato, inviato_bo_at: r.data.inviato_bo_at }));
      toast.success("Preventivo inviato al Back Office");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore invio al Back Office"); }
  };
  const segnaAvvisiLetti = async () => {
    try { await api.post(`/crm-quotes/${q.id}/avvisi/letti`); setQ((s) => ({ ...s, avvisi: (s.avvisi || []).map((a) => ({ ...a, letto: true })) })); load(); toast.success("Avvisi segnati come letti"); }
    catch { toast.error("Errore"); }
  };

  const rifiuta = async (row) => {
    if (!window.confirm(`Rifiutare il preventivo di ${row.cliente}?`)) return;
    const motivo = window.prompt("Motivo del rifiuto (opzionale):") || "";
    try { await api.post(`/crm-quotes/${row.id}/rifiuta`, { motivo }); toast.success("Preventivo rifiutato"); load(); }
    catch { toast.error("Errore"); }
  };

  const remove = async (row) => {    if (!window.confirm(`Eliminare il preventivo ${row.numero}?`)) return;
    try { await api.delete(`/crm-quotes/${row.id}`); toast.success("Eliminato"); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const ripristina = async (row) => {
    if (!window.confirm(`Ripristinare il preventivo ${row.numero || row.cliente}?`)) return;
    try { await api.post(`/crm-quotes/${row.id}/ripristina`); toast.success("Preventivo ripristinato"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore ripristino"); }
  };

  const doClone = async (mode) => {
    const row = cloneRow; setCloneRow(null);
    if (!row) return;
    try {
      const r = await api.post(`/crm-quotes/${row.id}/clone`, { mode });
      toast.success(mode === "revisione" ? `Revisione creata: ${r.data.numero}` : `Duplicato in ${r.data.numero}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore duplicazione"); }
  };

  const rimettiBozza = async () => {
    if (!window.confirm("Rimettere il preventivo in bozza?\n\nIl numero resta invariato, il PDF non sarà più scaricabile e sarà necessaria una nuova valutazione per riaprirlo.")) return;
    try {
      const r = await api.post(`/crm-quotes/${q.id}/rimetti-bozza`);
      setQ((s) => ({ ...s, stato: r.data.stato }));
      setGate(null);
      toast.success("Preventivo rimesso in bozza");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const [fissOpen, setFissOpen] = useState(false);
  const applyFissaggi = (righe, tettoConfig, moduloRiga) => {
    setQ((s) => {
      const base = s.righe.filter((r) => { const t = (r.tipologia || "").toLowerCase(); return !t.includes("modul") && !t.includes("fissagg"); });
      const locked = [...(moduloRiga ? [moduloRiga] : []), ...righe].map((r) => ({ ...r, locked: true }));
      const isMod = (r) => (r.tipologia || "").toLowerCase().includes("modul");
      const tc = { ...tettoConfig, lock: { signature: lockSig(locked), cliente_finale: tettoConfig.cliente || "", data: tettoConfig.calcolato_il || today(), consulenza_url: tettoConfig.consulenza_url || "", locked: true } };
      return { ...s, tetto_config: tc, righe: [...locked.filter(isMod), ...base, ...locked.filter((r) => !isMod(r))] };
    });
  };
  const sbloccaFissaggi = () => {
    if (!window.confirm("Sbloccando i fissaggi la configurazione non sarà più garantita e la pagina 3 (allegato sistema di fissaggio) NON verrà inclusa nel PDF. Continuare?")) return;
    setQ((s) => ({ ...s, righe: (s.righe || []).map((r) => ({ ...r, locked: false })), tetto_config: s.tetto_config ? { ...s.tetto_config, lock: { ...(s.tetto_config.lock || {}), locked: false } } : s.tetto_config }));
    toast.info("Fissaggi sbloccati: l'allegato non sarà incluso nel PDF finché non riconfiguri.");
  };

  const [tmplKey, setTmplKey] = useState(0);
  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setQ((s) => ({ ...s, righe: [...s.righe, ...(t.righe || []).map((r) => ({ ...r }))] }));
    toast.success(`Template "${t.nome}" aggiunto`);
    setTmplKey((k) => k + 1); // remount del Select per resettarlo
    // Fix Radix Select-in-Dialog: ripristina l'interattività del body dopo la selezione
    setTimeout(() => { if (document?.body) document.body.style.pointerEvents = ""; }, 50);
  };

  const downloadPdf = async (id) => {
    try {
      const r = await api.get(`/crm-quotes/${id}/pdf`, { responseType: "blob" });
      openPdfBlob(r.data, `Proposta_${id}.pdf`);
    } catch { toast.error("Errore generazione PDF"); }
  };

  return (
    <div className="space-y-6" data-testid="preventivatore-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-black"><Calculator className="h-7 w-7 text-primary" /> Preventivatore</h1>
          <p className="text-sm text-muted-foreground">Componi preventivi con i prodotti abilitati del listino. IVA predefinita 10%.</p>
        </div>
        <div className="flex items-center gap-2">
          {(canMgmt || user?.role === "head_of_partner") && <Button size="sm" variant="outline" onClick={() => setTmplMgr(true)} data-testid="prev-manage-templates"><Package className="mr-1.5 h-4 w-4" /> Gestisci template</Button>}
          <Button size="sm" onClick={openNew} data-testid="prev-new-btn"><Plus className="mr-1.5 h-4 w-4" /> Nuovo preventivo</Button>
        </div>
      </div>

      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-2" data-testid="prev-filters">
          <div className="flex-1 min-w-[200px]"><Label className="text-xs">Cerca</Label><Input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="Cliente o numero…" data-testid="prev-search" /></div>
          <div><Label className="text-xs">Stato</Label>
            <Select value={f.stato} onValueChange={(v) => setF({ ...f, stato: v })}>
              <SelectTrigger className="w-[170px]" data-testid="prev-filter-stato"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setF({ search: "", stato: "all" })}><RotateCcw className="mr-1.5 h-4 w-4" /> Azzera</Button>
        </div>
      </Card>

      <Card className="p-0" data-testid="prev-table-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Numero</TableHead><TableHead>Cliente</TableHead><TableHead>Data</TableHead>
              <TableHead>Stato</TableHead><TableHead className="text-right">kWp</TableHead>
              <TableHead className="text-right">Totale</TableHead>
              <TableHead className="text-right">IC</TableHead><TableHead className="text-right">Sconto appl.</TableHead>
              <TableHead>Autore</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></TableCell></TableRow>
              ) : attivi.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="py-10 text-center text-muted-foreground">Nessun preventivo. Crea il primo con "Nuovo preventivo".</TableCell></TableRow>
              ) : attivi.map((row) => (
                <TableRow key={row.id} data-testid={`prev-row-${row.id}`} className="cursor-pointer" onClick={() => openEdit(row)}>
                  <TableCell className="font-mono text-xs font-bold">{row.numero}</TableCell>
                  <TableCell className="font-semibold">{row.cliente}</TableCell>
                  <TableCell>{fmtDate(row.date)}</TableCell>
                  <TableCell><Badge variant={STATO_COLOR[row.stato] || "outline"}>{row.stato}</Badge></TableCell>
                  <TableCell className="text-right">{row.kwp_totale ? row.kwp_totale.toLocaleString("it-IT", { maximumFractionDigits: 2 }) : "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{eurN(row.totale)}</TableCell>
                  <TableCell className="text-right">{row.indice_contribuzione != null ? <Badge variant="outline" className={`font-bold ${icColorClass(row.indice_contribuzione, data.margin_config?.cap)}`} data-testid={`prev-row-ic-${row.id}`}>{row.indice_contribuzione.toLocaleString("it-IT", { maximumFractionDigits: 1 })}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right text-xs" data-testid={`prev-row-sconto-appl-${row.id}`}>{row.sconto_applicabile_eur != null ? eurN(row.sconto_applicabile_eur) : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{row.created_by_name}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCloneRow(row)} data-testid={`prev-clone-${row.id}`} title="Copia (nuovo o revisione)"><Copy className="h-4 w-4" /></Button>
                      {row.stato === "Aperto" && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEmailQuote(row)} data-testid={`prev-email-${row.id}`} title="Invia via email"><Mail className="h-4 w-4 text-primary" /></Button>}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadPdf(row.id)} data-testid={`prev-pdf-${row.id}`} title="Scarica PDF"><Download className="h-4 w-4 text-primary" /></Button>
                      {(row.avvisi || []).some((a) => !a.letto) && <span title="Disponibilità slittata: avvisa il cliente" data-testid={`prev-avviso-${row.id}`}><AlertTriangle className="h-4 w-4 text-amber-500" /></span>}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid={`prev-edit-${row.id}`}><Pencil className="h-4 w-4" /></Button>
                      {row.stato !== "Rifiutato" && row.stato !== "Chiuso" && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => rifiuta(row)} data-testid={`prev-reject-${row.id}`} title="Rifiuta preventivo"><Skull className="h-4 w-4 text-red-500" /></Button>}
                      {(user?.role !== "agency" || row.stato === "Bozza") && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(row)} data-testid={`prev-delete-${row.id}`} title={user?.role === "agency" ? "Elimina bozza" : "Elimina preventivo"}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Cimitero dei preventivi (Rifiutati) */}
      {cimitero.length > 0 && (
        <Card className="mt-6 border-red-500/30 p-0" data-testid="prev-cimitero-card">
          <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3">
            <Skull className="h-4 w-4 text-red-500" />
            <h3 className="font-heading text-sm font-black">Cimitero dei preventivi</h3>
            <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-bold text-red-500" data-testid="prev-cimitero-count">{cimitero.length}</span>
            <span className="text-[11px] text-muted-foreground">Preventivi rifiutati — puoi ripristinarli tra gli attivi.</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Numero</TableHead><TableHead>Cliente</TableHead><TableHead>Data</TableHead>
                <TableHead className="text-right">Totale</TableHead>
                <TableHead>Motivo rifiuto</TableHead><TableHead>Rifiutato da</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {cimitero.map((row) => (
                  <TableRow key={row.id} data-testid={`prev-cimitero-row-${row.id}`} className="cursor-pointer opacity-80" onClick={() => openEdit(row)}>
                    <TableCell className="font-mono text-xs font-bold">{row.numero}</TableCell>
                    <TableCell className="font-semibold">{row.cliente}</TableCell>
                    <TableCell>{fmtDate(row.date)}</TableCell>
                    <TableCell className="text-right font-semibold">{eurN(row.totale)}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-xs text-muted-foreground" title={row.motivo_rifiuto || ""}>{row.motivo_rifiuto || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{row.rifiutato_da || "—"}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadPdf(row.id)} data-testid={`prev-cimitero-pdf-${row.id}`} title="Scarica PDF"><Download className="h-4 w-4 text-primary" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => ripristina(row)} data-testid={`prev-cimitero-restore-${row.id}`} title="Ripristina tra gli attivi"><RotateCcw className="h-4 w-4 text-emerald-500" /></Button>
                        {user?.role !== "agency" && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(row)} data-testid={`prev-cimitero-delete-${row.id}`} title="Elimina definitivamente"><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Composer */}
      <Dialog open={q !== null} onOpenChange={(o) => !o && setQ(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="prev-composer" onInteractOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>{q?.id ? (q.numero ? `Modifica ${q.numero}` : "Modifica bozza") : "Nuovo preventivo"}</DialogTitle></DialogHeader>
          {(q?.avvisi || []).some((a) => !a.letto) && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 p-3 text-xs" data-testid="prev-avvisi-banner">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <div className="flex-1 space-y-1">
                {(q.avvisi || []).filter((a) => !a.letto).map((a) => <div key={a.id} className="font-medium text-amber-700 dark:text-amber-300">{a.messaggio}</div>)}
              </div>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={segnaAvvisiLetti} data-testid="prev-avvisi-letti">Segna letti</Button>
            </div>
          )}
          {q && (
            <div className="space-y-4">
              {q.stato !== "Bozza" && q.stato !== "Da Approvare" && (
                <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/[0.06] p-3 text-xs font-medium text-foreground" data-testid="prev-locked-banner">
                  <Lock className="h-4 w-4 shrink-0 text-primary" /> Preventivo {String(q.stato || "").toLowerCase()}: non modificabile. È consentita solo una nuova valutazione.
                </div>
              )}
              <fieldset disabled={q.stato !== "Bozza" && q.stato !== "Da Approvare"} className="m-0 min-w-0 space-y-4 border-0 p-0 disabled:opacity-70" data-testid="prev-fieldset">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div className="relative md:col-span-2">
                  <Label className="flex items-center gap-1.5 text-xs">Cliente
                    {q.partner_id && <Link to={`/partners/${q.partner_id}`} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline" data-testid="prev-cliente-anagrafica-link" title="Apri anagrafica cliente (nuova scheda)">· anagrafica ↗</Link>}
                  </Label>
                  <Input value={cliQ} onChange={(e) => searchCliente(e.target.value)} placeholder="Nome cliente o prospect…" data-testid="prev-cliente-input" />
                  {cliRes.length > 0 && (
                    <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-input bg-popover shadow-lg">
                      {cliRes.map((c, i) => (
                        <button key={c.type + c.id} type="button" onClick={() => pickCliente(c)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent" data-testid={`prev-cliente-result-${i}`}>
                          <span>{c.name} <span className="text-xs text-muted-foreground">{c.city}</span></span>
                          <Badge variant="secondary" className="text-[10px]">{c.type === "partner" ? "Cliente" : "Prospect"}</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div><Label className="text-xs">Data</Label><DateField value={q.date} onChange={(v) => setQ((s) => ({ ...s, date: v }))} testid="prev-data" /></div>
                <div><Label className="text-xs">Data richiesta consegna <span className="text-red-500">*</span></Label><DateField value={q.data_richiesta || ""} onChange={(v) => setQ((s) => ({ ...s, data_richiesta: v }))} invalid={!q.data_richiesta} testid="prev-data-richiesta" /></div>
                <div><Label className="text-xs">Stato</Label>
                  <div className="flex h-10 items-center"><Badge variant={STATO_COLOR[q.stato] || "outline"} data-testid="prev-stato-badge">{q.stato}</Badge></div>
                </div>
                <div className="md:col-span-4">
                  <Label className="text-xs">Modalità di pagamento</Label>
                  <Select value={q.modalita_pagamento || "__def__"} onValueChange={(v) => setQ((s) => ({ ...s, modalita_pagamento: v === "__def__" ? "" : v }))}>
                    <SelectTrigger className="w-full sm:w-[560px]" data-testid="prev-modalita-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__def__">Default del cliente — {q.modalita_default_cliente || "Ad avviso merce pronta"} (già approvato)</SelectItem>
                      {payMethods.map((m) => <SelectItem key={m.id} value={m.name} data-testid={`prev-modalita-opt-${m.id}`}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {q.modalita_pagamento && q.modalita_pagamento !== (q.modalita_default_cliente || "Ad avviso merce pronta") && (
                    <div className="mt-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400" data-testid="prev-modalita-warning">
                      Attenzione: modalità diversa dal default del cliente. Il preventivo dovrà essere confermato dal Back Office.
                    </div>
                  )}
                </div>
              </div>

              {/* line adder */}
              <div className="relative flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-primary/40 bg-primary/[0.04] p-2.5">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs font-semibold">Aggiungi dal listino</Label>
                  <Input value={lp} onChange={(e) => searchListino(e.target.value)} placeholder="Cerca codice o descrizione…" data-testid="prev-listino-search" />
                  {lpRes.length > 0 && (
                    <div className="absolute z-50 mt-1 max-h-56 w-[min(520px,90%)] overflow-y-auto rounded-md border border-input bg-popover shadow-lg">
                      {lpRes.slice(0, 25).map((p, i) => (
                        <button key={p.id} type="button" onClick={() => addFromListino(p)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent" data-testid={`prev-listino-result-${i}`}>
                          <span className="truncate"><span className="font-mono text-xs font-semibold">{p.codice}</span> — {p.descrizione}</span>
                          <span className="shrink-0 text-xs font-semibold">{eurN(p.prezzo)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {templates.length > 0 && (
                  <div className="min-w-[200px]">
                    <Label className="text-xs">Componi da template</Label>
                    <Select key={tmplKey} value="" onValueChange={applyTemplate}>
                      <SelectTrigger data-testid="prev-template-select"><SelectValue placeholder="Scegli un pacchetto…" /></SelectTrigger>
                      <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}{t.kwp ? ` · ${t.kwp} kWp` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {servizi.length > 0 && (
                  <div className="min-w-[200px]">
                    <Label className="text-xs">Aggiungi servizio</Label>
                    <Select value="" onValueChange={addServizio}>
                      <SelectTrigger data-testid="prev-servizio-select"><SelectValue placeholder="Scegli un servizio…" /></SelectTrigger>
                      <SelectContent>{servizi.map((s) => <SelectItem key={s.codice} value={s.codice} data-testid={`prev-servizio-opt-${s.codice}`}>{s.codice} — {(s.nome || "").slice(0, 42)}{(s.tipo === "pratica") ? " (per kWp)" : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {q.righe.length > 0 && serviziObbMancanti(q.righe).length > 0 && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-xs font-medium text-red-600 dark:text-red-400" data-testid="prev-servizi-obbligatori">
                  Attenzione: alcuni moduli richiedono un servizio obbligatorio non presente → {serviziObbMancanti(q.righe).map((s) => `${s.codice}`).join(" · ")}. Aggiungilo con "Aggiungi servizio" (obbligatorio per salvare).
                </div>
              )}

              {/* strutture fissaggio */}
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-primary/40 bg-primary/[0.04] p-2.5" data-testid="prev-fissaggi-box">
                <div className="text-xs text-muted-foreground">
                  {q.tetto_config ? (
                    fissLock && fissLock.signature ? (
                      fissLockValido ? (
                        <span className="inline-flex flex-wrap items-center gap-1.5 text-foreground" data-testid="prev-fissaggi-locked">
                          <Lock className="h-4 w-4 text-emerald-500" /> Fissaggi bloccati · Consulenza: <b>{fissLock.cliente_finale || q.tetto_config.cliente || "—"}</b>{fissLock.data ? ` del ${fmtDM(fissLock.data)}` : ""}{q.tetto_config.totali?.pannelli ? ` · ${q.tetto_config.totali.pannelli} pann.` : ""} — allegato incluso nel PDF
                          {q.tetto_config.consulenza_url && <a href={q.tetto_config.consulenza_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">· apri consulenza</a>}
                          <button type="button" onClick={sbloccaFissaggi} data-testid="prev-fissaggi-unlock" className="ml-1 rounded border border-input px-1.5 py-0.5 text-[11px] hover:bg-accent">Sblocca</button>
                        </span>
                      ) : (
                        <span className="inline-flex flex-wrap items-center gap-1.5 font-medium text-amber-600 dark:text-amber-400" data-testid="prev-fissaggi-invalid"><AlertTriangle className="h-4 w-4" /> Configurazione fissaggi non più valida (articoli modificati o sbloccati): l'allegato NON sarà incluso nel PDF. Riconfigura per rigenerarlo.</span>
                      )
                    ) : (
                      <span className="inline-flex flex-wrap items-center gap-1.5 text-foreground"><Ruler className="h-4 w-4 text-primary" /> Layout tetto allegato{q.tetto_config.totali?.pannelli ? ` · ${q.tetto_config.totali.pannelli} pann.` : ""}{q.tetto_config.consulenza_url ? <a href={q.tetto_config.consulenza_url} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="prev-tetto-link">· apri consulenza</a> : null}</span>
                    )
                  ) : "Dimensiona automaticamente le strutture di fissaggio dal layout tetto del configuratore."}
                </div>
                <Button type="button" size="sm" variant="outline" disabled={!(q.partner_id || q.prospect_id)} onClick={() => setFissOpen(true)} data-testid="prev-fissaggi-btn" title={(q.partner_id || q.prospect_id) ? "" : "Disponibile solo se il cliente è un Partner/Prospect collegato"}>
                  <Ruler className="mr-1.5 h-4 w-4" /> Dimensiona strutture di fissaggio
                </Button>
              </div>
              <FissaggiTettoDialog open={fissOpen} onOpenChange={setFissOpen} installerId={q.partner_id || q.prospect_id} moduleRow={q.righe.find((r) => (r.tipologia || "").toLowerCase().includes("modul"))} tettoConfig={q.tetto_config} onApply={applyFissaggi} />

              {/* lines */}
              <div className="overflow-x-auto rounded-lg border border-input shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs">
                    <tr>
                      <th className="p-2.5 text-left font-bold">Descrizione</th>
                      <th className="p-2.5 text-right w-20 font-bold">Q.tà</th>
                      <th className="p-2.5 text-right w-28 font-bold">Prezzo</th>
                      <th className="p-2.5 text-right w-28 font-bold">Totale</th>
                      <th className="p-2.5 w-8"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="prev-lines">
                    {q.righe.length === 0 ? (
                      <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">Nessuna riga. Aggiungi prodotti dal listino.</td></tr>
                    ) : lineGroups.map(([cat, items]) => {
                      const subtot = items.reduce((a, { r }) => a + lineTotal(r), 0);
                      const qta = items.reduce((a, { r }) => a + (+r.quantita || 0), 0);
                      const kwp = items.reduce((a, { r }) => a + (+r.quantita || 0) * (+r.kwp || 0), 0);
                      const isModCat = (cat || "").toLowerCase().includes("modul");
                      const eurWcat = isModCat && kwp > 0 ? subtot / (kwp * 1000) : null;
                      const acc = catAccent(cat);
                      return (
                        <Fragment key={cat}>
                          <tr className={`border-t-2 border-input ${acc.head}`} data-testid={`prev-cat-${cat}`}>
                            <td colSpan={3} className={`border-l-4 ${acc.rail} p-2.5`}>
                              <span className={`text-xs font-black uppercase tracking-wide ${acc.text}`}>{cat}</span>
                              <span className="ml-1 font-normal normal-case text-muted-foreground">· {items.length} art. · {qta.toLocaleString("it-IT", { maximumFractionDigits: 2 })} pz{kwp > 0 ? ` · ${kwp.toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp` : ""}{eurWcat != null ? ` · ${eurWcat.toLocaleString("it-IT", { maximumFractionDigits: 3 })} €/W` : ""}</span>
                            </td>
                            <td className="p-2 text-right" data-testid={`prev-cat-subtot-${cat}`}>
                              <span className={`inline-block rounded-md border px-2 py-0.5 text-xs font-bold ${acc.chip}`}>{eurN(subtot)}</span>
                            </td>
                            <td></td>
                          </tr>
                          {items.map(({ r, i }) => (
                            <tr key={i} className="border-t border-input/70 transition-colors hover:bg-muted/40" data-testid={`prev-line-${i}`}>
                              <td className={`border-l-4 ${acc.railSoft} p-1.5`}>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                  {r.codice && <span className="inline-block rounded bg-primary/15 px-1.5 py-0.5 font-mono text-xs font-bold text-primary" data-testid={`prev-line-${i}-code`}>{r.codice}</span>}
                                  <span className="text-sm font-medium text-foreground" data-testid={`prev-line-${i}-desc`}>{r.descrizione}</span>
                                </div>
                                <span className="text-[10px] text-muted-foreground">{r.um || "Pz"}{(r.tipologia || "").toLowerCase().includes("modul") && (+r.kwp || 0) > 0 ? ` · ${((+r.quantita || 0) * (+r.kwp || 0)).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp · ${((+r.prezzo_unitario || 0) / ((+r.kwp || 0) * 1000)).toLocaleString("it-IT", { maximumFractionDigits: 3 })} €/W` : ""}</span>
                                {r.codice && availByCode[(r.codice || "").toLowerCase()] && (() => { const av = availByCode[(r.codice || "").toLowerCase()]; const m = availMeta(av); return <div className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${m.color}`} data-testid={`prev-line-${i}-avail`}>{m.label}</div>; })()}
                              </td>
                              <td className="p-1.5"><Input type="number" step="1" min="0" inputMode="numeric" value={r.quantita} onChange={(e) => setLine(i, { quantita: e.target.value })} onKeyDown={(e) => { if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault(); }} disabled={r.locked} className="h-8 text-right text-xs font-semibold" data-testid={`prev-line-${i}-qty`} /></td>
                              <td className="p-1.5 text-right text-xs" data-testid={`prev-line-${i}-price`}>
                                {r.servizio_manuale ? (
                                  isBO ? <Input type="number" min="0" step="0.01" value={r.prezzo_unitario ?? ""} onChange={(e) => setLine(i, { prezzo_unitario: +e.target.value || 0 })} className="h-8 w-24 text-right text-xs" data-testid={`prev-line-${i}-price-input`} />
                                       : <span className="font-medium text-amber-600 dark:text-amber-400" title="Prezzo definito dal Back Office">da definire (BO)</span>
                                ) : <span className="text-muted-foreground">{eurN(r.prezzo_unitario)}</span>}
                              </td>
                              <td className="p-1.5 text-right font-bold">{eurN(lineTotal(r))}</td>
                              <td className="p-1.5 text-center">{r.locked ? <span title="Riga bloccata dalla consulenza fissaggi — sblocca dal riquadro strutture di fissaggio" data-testid={`prev-line-locked-${i}`} className="inline-flex text-emerald-500"><Lock className="h-4 w-4" /></span> : <button type="button" onClick={() => removeLine(i)} className="rounded p-1 text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-500" data-testid={`prev-line-remove-${i}`}><X className="h-4 w-4" /></button>}</td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* disponibilità magazzino */}
              {avail && avail.righe && avail.righe.length > 0 && (
                <div className="space-y-2 rounded-lg border border-input bg-muted/30 p-3" data-testid="prev-disponibilita">
                  <div className="flex items-center gap-2 text-sm font-bold"><Package className="h-4 w-4 text-primary" /> Disponibilità merce {availLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}</div>
                  <div className="text-xs" data-testid="prev-disponibilita-generale">
                    {avail.stato_generale === "da_ordinare" ? <span className="font-semibold text-red-600 dark:text-red-400">Alcuni articoli sono da ordinare senza data di arrivo — contattare il Back Office (blocca l'apertura)</span>
                      : avail.sconosciuti ? <span className="font-semibold text-amber-600 dark:text-amber-400">Disponibilità non verificabile per alcuni articoli (non a magazzino) — contattare il Back Office (blocca l'apertura)</span>
                        : avail.stato_generale === "disponibile" ? <span className="font-semibold text-emerald-600 dark:text-emerald-400">Tutta la merce è disponibile subito</span>
                          : <span className="font-semibold text-yellow-600 dark:text-yellow-400">Approntamento scaglionato — merce completa entro {fmtDM(avail.data_piena_generale)}</span>}
                    {avail.data_richiesta && avail.entro_richiesta === false && !avail.sconosciuti && avail.stato_generale !== "da_ordinare" && <span className="ml-1 text-amber-600 dark:text-amber-400">— oltre la data richiesta ({fmtDM(avail.data_richiesta)}): comunicare l'approntamento al cliente</span>}
                  </div>
                  <div className="space-y-1 border-t border-input pt-1.5">
                    {avail.righe.map((x, k) => (
                      <div key={k} className="flex items-start justify-between gap-3 text-[11px]" data-testid={`prev-disp-riga-${k}`}>
                        <span className="shrink-0 font-mono text-muted-foreground">{x.codice} · {x.quantita} pz</span>
                        <span className="flex items-start gap-1.5 text-right">
                          <span>{x.stato === "sconosciuto" ? <span className="text-muted-foreground">n/d magazzino</span> : (availDetail(x) || availMeta(x).label)}</span>
                          {canMagDetail && <button type="button" onClick={() => setMagDetail({ codice: x.codice, quantita: x.quantita })} data-testid={`prev-disp-info-${k}`} title="Dettaglio disponibilità (giacenza, uscite, saldo)" aria-label="Dettaglio disponibilità" className="-my-1 ml-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/10 active:bg-primary/20"><Info className="h-4 w-4" /></button>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* tipo ordine: spot / programmato */}
              <div className="space-y-2 rounded-lg border border-input bg-muted/30 p-3.5" data-testid="prev-tipo-ordine">
                <div className="text-sm font-bold">Tipo ordine</div>
                <div className="inline-flex flex-wrap gap-1 rounded-md border border-input p-0.5">
                  {[{ v: "spot", l: "Ordine spot" }, { v: "programmato", l: "Ordine programmato" }].map((o) => {
                    const dis = o.v === "programmato" && !!q.tetto_config;
                    return (
                    <button key={o.v} type="button" data-testid={`prev-tipo-ordine-${o.v}`} disabled={dis}
                      title={dis ? "Non disponibile per i preventivi da Configuratore" : undefined}
                      onClick={() => { if (dis) return; setQ((s) => o.v === "programmato"
                        ? { ...s, tipo_ordine: "programmato", consegna_modo: "parziale", trasporto_dettaglio: null }
                        : { ...s, tipo_ordine: "spot" }); }}
                      className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${(q.tipo_ordine || "spot") === o.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"} ${dis ? "cursor-not-allowed opacity-40" : ""}`}>{o.l}</button>
                    );
                  })}
                </div>
                {q.tetto_config && (
                  <div className="text-[11px] text-muted-foreground" data-testid="prev-tipo-ordine-config-note">I preventivi creati dal Configuratore (con layout tetto) non possono essere impostati come «Ordine programmato».</div>
                )}
                {q.tipo_ordine === "programmato" && (
                  <div className="space-y-1" data-testid="prev-tipo-ordine-note">
                    <div className="text-[11px] text-amber-600 dark:text-amber-400">Ordine programmato: spedizioni parziali obbligatorie (subito + saldo). Richiede importo di listino ≥ 30.000 €.</div>
                    {marginCalc?.programmato_bloccato ? (
                      <div className="rounded bg-red-500/10 px-2 py-1 text-[11px] font-semibold text-red-600 dark:text-red-400" data-testid="prev-scaglione-bloccato">
                        Ordine programmato non disponibile: importo di listino {eurN(marginCalc?.importo_listino)} inferiore a 50.000 €.
                      </div>
                    ) : marginCalc?.scaglione_programmato ? (
                      <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300" data-testid="prev-scaglione-badge">
                        Scaglione {marginCalc.scaglione_programmato === "s3" ? "3 · ≥150k" : marginCalc.scaglione_programmato === "s2" ? "2 · ≥100k" : "1 · ≥50k"} · listino {eurN(marginCalc?.importo_listino)}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* trasporto */}
              <div className="space-y-3 rounded-lg border border-y border-r border-input border-l-4 border-l-primary bg-primary/[0.04] p-3.5" data-testid="prev-trasporto">
                <div className="flex items-center gap-2 text-sm font-bold"><Truck className="h-4 w-4 text-primary" /> Trasporto</div>
                <div className="inline-flex flex-wrap gap-1 rounded-md border border-input p-0.5" data-testid="prev-consegna-tipo">
                  {[{ v: "vettore", l: "A mezzo vettore" }, { v: "ritiro", l: "Ritiro presso stabilimento (a carico cliente)" }, ...(data.can_use_dedicato ? [{ v: "dedicato", l: "Trasporti dedicati (Back Office)" }] : [])].map((o) => {
                    const locked = false;
                    return (
                      <button key={o.v} type="button" disabled={locked} onClick={() => !locked && setQ({ ...q, consegna_tipo: o.v })} data-testid={`prev-consegna-${o.v}`}
                        className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${q.consegna_tipo === o.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"} ${locked ? "cursor-not-allowed opacity-40" : ""}`}>{o.l}</button>
                    );
                  })}
                </div>
                {q.consegna_tipo === "ritiro" ? (
                  <div className="text-xs text-muted-foreground" data-testid="prev-consegna-ritiro-note">Ritiro presso stabilimento Fier — trasporto a carico del cliente. Nessuna spesa di trasporto applicata.</div>
                ) : (
                  <>
                    {q.tipo_ordine !== "programmato" && (
                    <div className="inline-flex flex-wrap gap-1 rounded-md border border-input p-0.5" data-testid="prev-consegna-modo">
                      {[{ v: "unica", l: "Consegna unica (a completamento)" }, { v: "parziale", l: "Spedizioni parziali (subito + saldo)" }].map((o) => {
                        const locked = q.tipo_ordine === "programmato" && o.v === "unica";
                        return (
                          <button key={o.v} type="button" disabled={locked} onClick={() => !locked && setQ({ ...q, consegna_modo: o.v })} data-testid={`prev-consegna-modo-${o.v}`}
                            className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${(q.consegna_modo || "unica") === o.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"} ${locked ? "cursor-not-allowed opacity-40" : ""}`}>{o.l}</button>
                        );
                      })}
                    </div>
                    )}
                    {q.tipo_ordine !== "programmato" && (q.consegna_modo === "parziale") && <div className="text-[11px] text-muted-foreground" data-testid="prev-consegna-modo-note">La merce pronta viene spedita subito, il saldo alla disponibilità — possibile 2° costo di trasporto (calcolato per lotto).</div>}
                    <label className="flex w-fit items-center gap-1.5 text-xs font-medium" data-testid="prev-consegna-sede-label">
                      <input type="checkbox" checked={!!q.consegna_sede_cliente} onChange={(e) => applySedeCliente(e.target.checked)} data-testid="prev-consegna-sede" /> Spedizione presso sede del cliente
                    </label>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                      <div className="md:col-span-2"><Label className="text-xs">Indirizzo di consegna</Label><Input value={q.consegna_indirizzo} onChange={(e) => setQ({ ...q, consegna_indirizzo: e.target.value, consegna_sede_cliente: false })} placeholder="Via e civico" className="h-8 text-xs" data-testid="prev-consegna-indirizzo" /></div>
                      <div><Label className="text-xs">CAP</Label><Input value={q.consegna_cap} onChange={(e) => setQ({ ...q, consegna_cap: e.target.value, consegna_sede_cliente: false })} className="h-8 text-xs" data-testid="prev-consegna-cap" /></div>
                      <div><Label className="text-xs">Città</Label><Input value={q.consegna_citta} onChange={(e) => setQ({ ...q, consegna_citta: e.target.value, consegna_sede_cliente: false })} className="h-8 text-xs" data-testid="prev-consegna-citta" /></div>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="min-w-[220px]">
                        <Label className="text-xs">Provincia di consegna</Label>
                        <Select value={q.trasporto_zone} onValueChange={(v) => setQ({ ...q, trasporto_zone: v, consegna_provincia: v, consegna_sede_cliente: false })}>
                          <SelectTrigger data-testid="prev-trasporto-zona"><SelectValue placeholder="Seleziona provincia…" /></SelectTrigger>
                          <SelectContent className="max-h-72">{provinces.map((p) => <SelectItem key={p.sigla} value={p.sigla}>{p.sigla} — {p.nome}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {q.consegna_tipo !== "dedicato" && (
                      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="prev-trasporto-flags-auto">
                        <span className="text-muted-foreground">Supplementi (automatici):</span>
                        {q.trasporto_non_sovrapponibile && <span className="rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 font-medium text-amber-600 dark:text-amber-400" data-testid="prev-trasporto-flag-nonsovr">Non sovrapponibile</span>}
                        {q.trasporto_fasci && <span className="rounded-full border border-sky-500/40 bg-sky-500/15 px-2 py-0.5 font-medium text-sky-600 dark:text-sky-400" data-testid="prev-trasporto-flag-fasci">Fasci</span>}
                        {q.trasporto_adr && <span className="rounded-full border border-red-500/40 bg-red-500/15 px-2 py-0.5 font-medium text-red-600 dark:text-red-400" data-testid="prev-trasporto-flag-adr">ADR</span>}
                        {!q.trasporto_non_sovrapponibile && !q.trasporto_fasci && !q.trasporto_adr && <span className="text-muted-foreground" data-testid="prev-trasporto-flag-none">nessuno</span>}
                        {calcTrasp && <span className="flex items-center gap-1.5 text-muted-foreground" data-testid="prev-trasporto-calc-status"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Calcolo in corso…</span>}
                      </div>
                      )}
                      {q.consegna_tipo !== "dedicato" && q.consegna_tipo !== "ritiro" && (
                        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground" data-testid="prev-trasporto-nonsovr-manuale-wrap">
                          <input type="checkbox" checked={!!q.trasporto_non_sovr_manuale} onChange={(e) => setQ({ ...q, trasporto_non_sovr_manuale: e.target.checked })} data-testid="prev-trasporto-nonsovr-manuale" />
                          Forza «non sovrapponibile» (manuale)
                        </label>
                      )}
                    </div>
                    {q.consegna_tipo !== "dedicato" && q.consegna_tipo !== "ritiro" && (
                      <p className="text-[11px] text-muted-foreground" data-testid="prev-trasporto-nota-servizi">Trasporto comprensivo di: preavviso telefonico, consegna in fascia oraria ristretta e sponda idraulica (inclusi nel prezzo).</p>
                    )}
                    {q.consegna_tipo === "dedicato" && (
                      <div className="space-y-1.5 rounded-md border border-primary/40 bg-primary/5 p-2.5" data-testid="prev-trasporto-dedicato">
                        <Label className="text-xs font-semibold text-primary">Trasporti dedicati — prezzo inserito dal Back Office</Label>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Prezzo trasporto (€)</span>
                          <Input type="number" min="0" step="1" value={q.trasporto_eur || ""}
                            onChange={(e) => { const v = +e.target.value || 0; setQ((s) => ({ ...s, trasporto_eur: v, trasporto_dettaglio: { dedicato: true, calcolabile: true, totale: v } })); }}
                            className="h-8 w-32 text-right text-xs" data-testid="prev-trasporto-dedicato-input" />
                        </div>
                        <div className="text-[11px] text-muted-foreground">Le altre informazioni logistiche restano come nel trasporto normale.</div>
                      </div>
                    )}
                    {q.consegna_tipo !== "dedicato" && q.trasporto_dettaglio?.parziale ? (
                      <div className="space-y-2 text-xs" data-testid="prev-trasporto-dettaglio-parziale">
                        {(q.trasporto_dettaglio.lotti || []).map((lt, li) => (
                          <div key={li} className="rounded-md border border-input bg-background/60 p-2" data-testid={`prev-trasporto-lotto-${li}`}>
                            <div className="flex items-center justify-between font-semibold text-foreground">
                              <span>{li + 1}ª spedizione · {lt.label}</span>
                              <span>{lt.det?.calcolabile ? eurN(lt.det.totale) : <span className="text-red-500">non calcolabile</span>}</span>
                            </div>
                            <div className="mt-0.5 text-[11px] text-muted-foreground">{lt.data ? `Disponibile entro ${fmtDM(lt.data)}` : "Pronta subito"}</div>
                            {(lt.items || []).length > 0 && (
                              <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                                {lt.items.map((it, ii) => (
                                  <li key={ii}>• <span className="font-medium text-foreground">{it.quantita} pz</span> — {it.codice || it.descrizione}</li>
                                ))}
                              </ul>
                            )}
                            {lt.det?.calcolabile && (
                              <div className="mt-1 text-[11px] text-muted-foreground">Peso tassato {lt.det.peso_tassato_kg} kg · scaglione {lt.det.scaglione} · {lt.det.pallet_totali} pallet · tariffa {lt.det.tariffa_eur_100kg} €/100kg</div>
                            )}
                          </div>
                        ))}
                        <div className="font-semibold text-foreground">Totale trasporto (2 spedizioni): {eurN(q.trasporto_dettaglio.totale)}</div>
                      </div>
                    ) : (q.consegna_tipo !== "dedicato" && q.trasporto_dettaglio?.calcolabile) && (
                      <div className="text-xs text-muted-foreground" data-testid="prev-trasporto-dettaglio">
                        Peso tassato {q.trasporto_dettaglio.peso_tassato_kg} kg · scaglione {q.trasporto_dettaglio.scaglione} · {q.trasporto_dettaglio.pallet_totali} pallet · tariffa {q.trasporto_dettaglio.tariffa_eur_100kg} €/100kg → <span className="font-semibold text-foreground">Totale {eurN(q.trasporto_dettaglio.totale)}</span>{q.trasporto_dettaglio.incidenza_pct != null ? ` (${q.trasporto_dettaglio.incidenza_pct}% sul valore)` : ""}
                      </div>
                    )}
                    {q.consegna_tipo !== "dedicato" && q.trasporto_dettaglio && !q.trasporto_dettaglio.calcolabile && (
                      <div className="text-xs text-red-500" data-testid="prev-trasporto-nocalc">Trasporto non calcolabile: peso/volume mancanti sui prodotti selezionati.</div>
                    )}
                  </>
                )}
                {q.tipo_ordine === "programmato" && (
                  <PianoConsegne
                    righe={q.righe}
                    consegnaTipo={q.consegna_tipo}
                    defaultZone={q.trasporto_zone || q.consegna_provincia}
                    provinces={provinces}
                    valore={computeTotals(q.righe, q.iva_pct, q.sconto_globale_pct, 0).imponibile_scontato}
                    piano={q.consegna_piano}
                    onChange={(p) => setQ((s) => ({ ...s, consegna_piano: p }))}
                    onTotale={(tt) => setQ((s) => ({ ...s, trasporto_eur: (s.consegna_tipo === "ritiro" ? 0 : tt) }))}
                  />
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="w-28 text-xs">Sconto globale</Label>
                    <div className="inline-flex overflow-hidden rounded-md border border-input" data-testid="prev-sconto-mode">
                      {[["pct", "%"], ["eur", "€"]].map(([m, lbl]) => (
                        <button key={m} type="button" onClick={() => setScontoMode(m)}
                          data-testid={`prev-sconto-mode-${m}`}
                          className={`px-2.5 py-1 text-xs font-semibold transition-colors ${scontoMode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
                      ))}
                    </div>
                    {scontoMode === "pct" ? (
                      <Input type="number" step="0.01" value={q.sconto_globale_pct}
                        onChange={(e) => { let v = e.target.value; if (scontoMaxPct != null && +v > scontoMaxPct) v = +scontoMaxPct.toFixed(2); setQ({ ...q, sconto_globale_pct: v }); }}
                        className="h-8 w-28 text-right" data-testid="prev-sconto-glob" />
                    ) : (
                      <Input type="number" step="1" value={Math.round(appliedScontoEur)}
                        onChange={(e) => { let eur = +e.target.value || 0; if (scontoMaxEur != null && eur > scontoMaxEur) eur = scontoMaxEur; setQ({ ...q, sconto_globale_pct: (totals.imponibile > 0 ? +(eur / totals.imponibile * 100).toFixed(4) : 0) }); }}
                        className="h-8 w-28 text-right" data-testid="prev-sconto-glob-eur" />
                    )}
                  </div>
                  {scontoApplEur != null && (
                    <div className={`space-y-2 rounded-md border p-2.5 ${sottoMargine ? "border-red-400/60 bg-red-400/5" : "border-primary/40 bg-primary/5"}`} data-testid="prev-sconto-applicabile">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs font-semibold"><TrendingUp className="h-3.5 w-3.5 text-primary" /> Sconto applicabile</span>
                        <span className="text-sm font-black text-primary" data-testid="prev-sconto-appl-value">{eurN(scontoApplEur)}</span>
                      </div>
                      <Slider
                        min={0}
                        max={Math.max(1, Math.round(scontoMaxEur))}
                        step={1}
                        value={[Math.min(Math.round(appliedScontoEur), Math.round(scontoMaxEur))]}
                        onValueChange={([v]) => setQ((s) => ({ ...s, sconto_globale_pct: (totals.imponibile > 0 ? +(v / totals.imponibile * 100).toFixed(4) : 0) }))}
                        disabled={!(scontoMaxEur > 0) || !(totals.imponibile > 0)}
                        data-testid="prev-sconto-slider"
                      />
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        <span>Applicato: <b className={sottoMargine ? "text-red-600 dark:text-red-400" : "text-foreground"}>{eurN(appliedScontoEur)}</b></span>
                        {canFin && <span>Margine target {fmtPct(totals.margine_target_pct)}</span>}
                      </div>
                      {sottoMargine && (
                        <div className="text-[11px] font-medium text-red-600 dark:text-red-400" data-testid="prev-sconto-sotto-margine">
                          Oltre il margine minimo (extra fino a +10%): il preventivo verrà inviato al Back Office per approvazione.
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2"><Label className="w-28 text-xs">IVA %</Label>
                    <div className="inline-flex gap-1 rounded-md border border-input p-0.5" data-testid="prev-iva">
                      {[10, 22].map((v) => (
                        <button key={v} type="button" onClick={() => setQ({ ...q, iva_pct: v })}
                          data-testid={`prev-iva-${v}`}
                          className={`rounded px-4 py-1 text-xs font-semibold transition-colors ${(+q.iva_pct === v) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{v}%</button>
                      ))}
                    </div>
                  </div>
                  <div><Label className="text-xs">Note</Label><Textarea value={q.note} onChange={(e) => setQ({ ...q, note: e.target.value })} className="min-h-[60px] text-sm" data-testid="prev-note" /></div>
                </div>
                <Card className="space-y-1 p-3 text-sm" data-testid="prev-total">
                  <div className="flex justify-between"><span className="text-muted-foreground">Imponibile</span><span>{eurN(totals.imponibile)}</span></div>
                  {(+q.sconto_globale_pct || 0) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Imponibile scontato</span><span>{eurN(totals.imponibile_scontato)}</span></div>}
                  {(+q.trasporto_eur || 0) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Trasporto{q.trasporto_dettaglio?.zona ? ` · ${q.trasporto_dettaglio.zona}` : ""}</span><span>{eurN(totals.trasporto_eur)}</span></div>}
                  {(totals.raee_eur || 0) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Eco-contributo RAEE ({totals.raee_moduli} moduli)</span><span>{eurN(totals.raee_eur)}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">IVA ({q.iva_pct}%)</span><span>{eurN(totals.iva_eur)}</span></div>
                  <div className="flex justify-between border-t border-input pt-1 text-base font-black"><span>Totale</span><span>{eurN(totals.totale)}</span></div>
                  <div className="flex justify-between text-xs text-muted-foreground"><span>Potenza</span><span>{totals.kwp_totale ? totals.kwp_totale.toLocaleString("it-IT", { maximumFractionDigits: 2 }) + " kWp" : "—"}</span></div>
                  {canFin && <div className="flex justify-between border-t border-input pt-1 text-xs text-muted-foreground"><span>Costo / Margine</span><span>{eurN(totals.costo_totale)} · {totals.margine_pct.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%</span></div>}
                  {totals.indice_contribuzione != null && (
                    <div className="flex items-center justify-between border-t border-input pt-1 text-xs" data-testid="prev-indice-contribuzione">
                      <span className="text-muted-foreground">Indice di contribuzione</span>
                      <Badge variant="outline" className={`font-bold ${icColorClass(totals.indice_contribuzione, data.margin_config?.cap)}`} data-testid="prev-ic-value">{totals.indice_contribuzione.toLocaleString("it-IT", { maximumFractionDigits: 1 })}</Badge>
                    </div>
                  )}
                  {totals.provvigione_pct != null && totals.provvigione_pct > 0 && (
                    <div className="flex items-center justify-between border-t border-input pt-1 text-xs" data-testid="prev-provvigione">
                      <span className="text-muted-foreground">Provvigione ({fmtPct(totals.provvigione_pct)})</span>
                      <span className="font-semibold text-foreground" data-testid="prev-provvigione-value">{eurN(totals.provvigione_eur)}</span>
                    </div>
                  )}
                  {q.cliente_is_partner && (
                    <div className="mt-1 flex items-center justify-between gap-2 rounded-md bg-primary px-2.5 py-2 text-primary-foreground" data-testid="prev-vesta">
                      <span className="flex items-center gap-1.5 text-xs font-bold"><VestaBadge className="h-5 w-5" /> Punti Vesta potenziali</span>
                      <span className="text-lg font-black" data-testid="prev-vesta-value">{(Math.round((totals.imponibile_scontato || 0) * 0.03 / 120 * 10) / 10).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
                    </div>
                  )}
                </Card>
              </div>
              </fieldset>

              <div className="flex flex-wrap items-center justify-end gap-2">
                {gate && (
                  <div className="mr-auto w-full space-y-1.5 rounded-lg border border-input bg-muted/30 p-3" data-testid="prev-gate">
                    <div className="flex flex-wrap gap-3 text-xs font-semibold">
                      {[["Disponibilità", gate.semafori.disponibilita], ["Margine", gate.semafori.margine], ["Affidamento", gate.semafori.rischio]].map(([l, s]) => {
                        const box = s === "green" ? "bg-emerald-400/15 text-emerald-700 dark:text-emerald-300" : s === "yellow" ? "bg-amber-400/15 text-amber-700 dark:text-amber-300" : "bg-red-400/15 text-red-700 dark:text-red-300";
                        const dot = s === "green" ? "bg-emerald-500" : s === "yellow" ? "bg-amber-500" : "bg-red-500";
                        return (
                          <span key={l} className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 ${box}`}>
                            <span className={`h-2 w-2 rounded-full ${dot}`} /> {l}
                          </span>
                        );
                      })}
                    </div>
                    {gate.fido_info && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-testid="prev-gate-fido">
                        <span className="font-semibold text-foreground">Affidamento:</span>
                        {gate.fido_info.fido == null
                          ? <span className="text-red-600 dark:text-red-400">Nessun fido concesso</span>
                          : <>
                              <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">Fido: {eurN(gate.fido_info.fido)}</span>
                              <span>Esposizione: {eurN(gate.fido_info.esposizione)}</span>
                              <span className={gate.fido_info.residuo < 0 ? "font-medium text-red-600 dark:text-red-400" : ""}>Residuo: {eurN(gate.fido_info.residuo)}</span>
                              <span>Preventivo: {eurN(gate.fido_info.importo_preventivo)}</span>
                              {gate.fido_info.sconfino > 0 && <span className="font-semibold text-red-600 dark:text-red-400">Sconfino: {eurN(gate.fido_info.sconfino)}</span>}
                            </>}
                        <span className="italic">stima da commesse aperte</span>
                      </div>
                    )}
                    {gate.ai && <div className="text-xs text-muted-foreground" data-testid="prev-gate-ai">{gate.ai}</div>}
                    {gate.blockers?.length > 0 && <ul className="list-disc pl-5 text-xs text-red-600 dark:text-red-400">{gate.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>}
                    <div className="flex gap-2 pt-1">
                      {(q.stato === "Bozza" || q.stato === "Da Approvare") && gate.can_auto_open && <Button size="sm" onClick={() => apriPreventivo(false)} data-testid="prev-gate-conferma">Conferma apertura</Button>}
                      {(q.stato === "Bozza" || q.stato === "Da Approvare") && !gate.can_auto_open && gate.can_force && <Button size="sm" variant="destructive" onClick={() => apriPreventivo(true)} data-testid="prev-gate-forza">Forza apertura</Button>}
                      {(q.stato === "Bozza" || q.stato === "Da Approvare") && !gate.can_auto_open && <Button size="sm" variant="secondary" onClick={inviaBackoffice} data-testid="prev-gate-invia-bo"><Send className="mr-2 h-4 w-4" /> Invia al Back Office</Button>}
                      {q.inviato_bo_at && <span className="inline-flex items-center text-xs font-medium text-emerald-600 dark:text-emerald-400" data-testid="prev-inviato-bo">✓ Inviato al Back Office</span>}
                      <Button size="sm" variant="ghost" onClick={() => setGate(null)}>Chiudi</Button>
                    </div>
                  </div>
                )}
                <Button variant="outline" onClick={() => setQ(null)}>Annulla</Button>
                {q.id && q.stato !== "Bozza" && <Button variant="outline" onClick={() => downloadPdf(q.id)} data-testid="prev-pdf-btn"><Download className="mr-2 h-4 w-4" /> Scarica PDF</Button>}
                {q.id && q.stato === "Aperto" && <Button variant="outline" onClick={rimettiBozza} data-testid="prev-rimetti-bozza-btn"><RotateCcw className="mr-2 h-4 w-4" /> Rimetti in bozza</Button>}
                {(q.stato === "Bozza" || q.stato === "Da Approvare") && <Button variant="outline" onClick={valutaPreventivo} disabled={gating} data-testid="prev-valuta-btn">{gating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator className="mr-2 h-4 w-4" />} Valuta e Apri</Button>}
                {q.id && q.stato === "Aperto" && <Button variant="outline" onClick={valutaPreventivo} disabled={gating} data-testid="prev-rivaluta-btn">{gating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator className="mr-2 h-4 w-4" />} Nuova valutazione</Button>}
                {(q.stato === "Bozza" || q.stato === "Da Approvare") && <Button onClick={save} disabled={saving} data-testid="prev-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva preventivo</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TemplateManager open={tmplMgr} onOpenChange={setTmplMgr} onSaved={loadTemplates} />
      <EmailQuoteDialog quote={emailQuote} open={!!emailQuote} onClose={() => setEmailQuote(null)} />

      <Dialog open={!!cloneRow} onOpenChange={(o) => !o && setCloneRow(null)}>
        <DialogContent className="max-w-md" data-testid="prev-clone-dialog">
          <DialogHeader><DialogTitle>Copia preventivo {cloneRow?.numero || ""}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">Vuoi creare una <b>revisione</b> dello stesso preventivo (mantiene il numero base con suffisso <span className="font-mono">rev.N</span>) oppure un <b>nuovo preventivo</b> con numero indipendente?</DialogDescription>
          <div className="mt-2 flex flex-col gap-2">
            <Button onClick={() => doClone("revisione")} disabled={!cloneRow?.numero} data-testid="prev-clone-revisione">
              <Copy className="mr-2 h-4 w-4" /> Nuova revisione {cloneRow?.numero ? `di ${cloneRow.numero}` : ""}
            </Button>
            {!cloneRow?.numero && <span className="text-xs text-muted-foreground">La revisione non è disponibile: il preventivo è ancora in Bozza (senza numero).</span>}
            <Button variant="outline" onClick={() => doClone("nuovo")} data-testid="prev-clone-nuovo">
              <Plus className="mr-2 h-4 w-4" /> Nuovo preventivo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <MagazzinoDetailDialog open={!!magDetail} onOpenChange={(o) => !o && setMagDetail(null)} codice={magDetail?.codice} quantita={magDetail?.quantita || 0} dataRichiesta={q?.data_richiesta || null} />
    </div>
  );
}
